'use client'
import React, { useState, useRef, useEffect, useCallback } from 'react'
import classNames from 'classnames'
import { CMSLink } from '../../components/CMSLink'
import { ButtonField } from '@/payload-types'

type Position = 'top' | 'bottom' | 'left' | 'right'

type TooltipProps = {
  children: React.ReactNode
  content: React.ReactNode
  className?: string
  position?: Position
  cmsButton?: ButtonField
  showTooltipButton?: boolean
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  className,
  position = 'bottom',
  cmsButton,
  showTooltipButton = false,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const [isVisible, setIsVisible] = useState(false)
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({})
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false)
  const [isHoveringTrigger, setIsHoveringTrigger] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState<Position>(position)
  const [tooltipOffset, setTooltipOffset] = useState({ x: 0, y: 0 })

  const updatePosition = useCallback(() => {
    if (!tooltipRef.current || !triggerRef.current) return

    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const triggerRect = triggerRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let newPosition = tooltipPosition
    const newOffset = { x: 0, y: 0 }

    // Check horizontal overflow
    if (tooltipRect.right > viewportWidth) {
      newOffset.x = viewportWidth - tooltipRect.right - 10
    }
    if (tooltipRect.left < 0) {
      newOffset.x = -tooltipRect.left + 10
    }

    // Check vertical overflow and adjust position if needed
    if (tooltipPosition === 'top' && tooltipRect.top < 0) {
      newPosition = 'bottom'
    } else if (tooltipPosition === 'bottom' && tooltipRect.bottom > viewportHeight) {
      newPosition = 'top'
    }

    setTooltipOffset(newOffset)
    setTooltipPosition(newPosition)
  }, [tooltipPosition])

  useEffect(() => {
    if (isVisible) {
      updatePosition()
      window.addEventListener('resize', updatePosition)
      window.addEventListener('scroll', updatePosition)
    }

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition)
    }
  }, [isVisible, updatePosition])

  const handleClickOutside = (event: MouseEvent) => {
    if (
      tooltipRef.current &&
      !tooltipRef.current.contains(event.target as Node) &&
      triggerRef.current &&
      !triggerRef.current.contains(event.target as Node)
    ) {
      setIsVisible(false)
    }
  }

  useEffect(() => {
    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isVisible])

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsHoveringTrigger(true)
    setIsVisible(true)
  }

  const handleMouseLeave = () => {
    setIsHoveringTrigger(false)
    timeoutRef.current = setTimeout(() => {
      if (!isHoveringTooltip) {
        setIsVisible(false)
      }
    }, 200)
  }

  const handleTouchStart = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsHoveringTrigger(true)
    setIsVisible(true)
  }

  const handleTouchEnd = () => {
    setIsHoveringTrigger(false)
    timeoutRef.current = setTimeout(() => {
      if (!isHoveringTooltip) {
        setIsVisible(false)
      }
    }, 200)
  }

  const handleClick = () => {
    setIsVisible(!isVisible)
  }

  return (
    <div className="relative" ref={triggerRef}>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={handleClick}
        className={classNames('relative', className)}
      >
        {children}
      </div>
      {isVisible && (
        <div
          ref={tooltipRef}
          className={classNames(
            'absolute z-50 bg-bg-subtle border border-neutral-95 rounded-sm p-3 shadow-md',
            'w-[200px] sm:w-[240px]',
            {
              'bottom-full left-1/2 -translate-x-1/2 mb-2': tooltipPosition === 'top',
              'top-full left-1/2 -translate-x-1/2 mt-2': tooltipPosition === 'bottom',
              'right-full top-1/2 -translate-y-1/2 mr-2': tooltipPosition === 'left',
              'left-full top-1/2 -translate-y-1/2 ml-2': tooltipPosition === 'right',
            },
          )}
          style={{
            ...tooltipStyle,
            transform: `translate(${tooltipOffset.x}px, ${tooltipOffset.y}px)`,
          }}
          onMouseEnter={() => setIsHoveringTooltip(true)}
          onMouseLeave={() => setIsHoveringTooltip(false)}
          onTouchStart={() => setIsHoveringTooltip(true)}
          onTouchEnd={() => setIsHoveringTooltip(false)}
        >
          {typeof content === 'string' ? (
            <p className="text-body-XS-regular text-text-base">{content}</p>
          ) : (
            content
          )}
          {showTooltipButton && cmsButton && (
            <CMSLink
              {...cmsButton}
              className="!text-label-XS-semibold text-text-inverted p-2 mt-2 w-full"
            />
          )}
        </div>
      )}
    </div>
  )
}
